// DAS-2107: Reusable analysis card for PortfolioAnalysisView grid
// Accepts icon, title, stats (mini-stat lines), and onClick handler.
// Designed for MUI, responsive, and data-source agnostic.

import React from 'react';
import {
  Card,
  CardActionArea,
  CardContent,
  Box,
  Typography,
  Avatar,
  Skeleton,
  Chip,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

const AnalysisCard = ({
  icon,
  title,
  stats = [],
  color = '#1976d2',
  onClick,
  loading = false,
  disabled = false,
}) => {
  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        opacity: disabled ? 0.5 : 1,
        transition: 'box-shadow 0.2s, transform 0.15s',
        '&:hover': disabled ? {} : {
          boxShadow: 4,
          transform: 'translateY(-2px)',
        },
      }}
    >
      <CardActionArea
        onClick={disabled ? undefined : onClick}
        disabled={disabled}
        sx={{ height: '100%', alignItems: 'flex-start' }}
      >
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Box display="flex" alignItems="flex-start" gap={1.5}>
            <Avatar
              sx={{
                bgcolor: `${color}14`,
                color: color,
                width: 40,
                height: 40,
                flexShrink: 0,
              }}
            >
              {icon}
            </Avatar>

            <Box flex={1} minWidth={0}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, lineHeight: 1.3 }}
                >
                  {title}
                </Typography>
                <ChevronRightIcon
                  sx={{ fontSize: 18, color: 'text.disabled', flexShrink: 0, ml: 0.5 }}
                />
              </Box>

              {loading ? (
                <Box mt={0.5}>
                  <Skeleton width="70%" height={16} />
                  <Skeleton width="50%" height={16} />
                </Box>
              ) : stats.length > 0 ? (
                <Box mt={0.5} display="flex" flexDirection="column" gap={0.25}>
                  {stats.map((stat, idx) => (
                    <Box key={idx} display="flex" alignItems="center" gap={0.75}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ lineHeight: 1.4 }}
                      >
                        {stat.label}
                      </Typography>
                      {stat.chip ? (
                        <Chip
                          label={stat.value}
                          size="small"
                          color={stat.chipColor || 'default'}
                          sx={{ height: 18, fontSize: '0.65rem' }}
                        />
                      ) : (
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 600,
                            lineHeight: 1.4,
                            color: stat.color || 'text.primary',
                          }}
                        >
                          {stat.value}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5 }}>
                  No data
                </Typography>
              )}
            </Box>
          </Box>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

export default AnalysisCard;
